import { createFileRoute, redirect, Link } from '@tanstack/react-router'
import { Typography, Button, Box, Container } from '@mui/material'
import { useAuthStore } from '../stores/auth-store'
import { SiteNav } from '../components/site/SiteNav'
import { AccountMesh } from '../components/site/AccountMesh'
import { SiteFooter } from '../components/site/SiteFooter'
import { TIERS, formatPrice, formatCadence, type Currency } from '../config/pricing'
import { useCurrency } from '../hooks/useCurrency'

export const Route = createFileRoute('/')({
  component: Index,
  beforeLoad: () => {
    const { isAuthenticated } = useAuthStore.getState()
    if (isAuthenticated) {
      throw redirect({ to: '/dashboard' })
    }
  },
})

/**
 * Marketing page.
 *
 * Reads as a PRODUCT, not a feature list. The previous version was written
 * like a spec sheet -- hairline rules, dense claims, every invariant on
 * display -- which is honest and gives a visitor no idea what they get.
 *
 * Structure follows the trust-and-authority pattern that fintech converts on:
 * one outcome in the hero, evidence immediately under it, then three things
 * the product does stated as outcomes, then a single path forward. The
 * "what it will not do" section stays, because for money software candour
 * IS the sales pitch -- it is the most credible thing on the page.
 */
function Index() {
  const [currency, setCurrency] = useCurrency()

  // One accent, used only on things you can act on. A page where everything
  // is emphasised has emphasised nothing.
  const stat = { fontSize: { xs: '1.9rem', md: '2.4rem' }, fontWeight: 700,
    letterSpacing: '-0.03em', lineHeight: 1.1 }

  return (
    <Box sx={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <SiteNav />

      {/* ── Hero. One outcome, one action. ──────────────────────────────── */}
      <Box sx={{ position: 'relative' }}>
        {/* Accounts as nodes, value moving between them. Sits behind the
            headline and fades out where the copy is, so it never costs the
            text its contrast. */}
        <AccountMesh />
        <Container maxWidth="lg" sx={{ pt: { xs: 9, md: 16 }, pb: { xs: 7, md: 12 },
          position: 'relative' }}>
        <Box sx={{ maxWidth: 820 }}>
          <Typography sx={{
            fontSize: '0.8rem', letterSpacing: '0.08em', textTransform: 'uppercase',
            color: 'var(--text-faint)', mb: 3,
          }}>
            Built in Canada, for Canadian households
          </Typography>

          <Typography component="h1" sx={{
            fontSize: { xs: '2.6rem', sm: '3.4rem', md: '4.2rem' },
            fontWeight: 700, letterSpacing: '-0.04em', lineHeight: 1.05, mb: 3,
          }}>
            Know what you&rsquo;re actually worth.
          </Typography>

          <Typography sx={{
            fontSize: { xs: '1.05rem', md: '1.25rem' }, lineHeight: 1.6,
            color: 'var(--text-secondary)', maxWidth: 620, mb: 5,
          }}>
            Every account, every holding, every dollar &mdash; adding up to one
            number you can trust. For the two of you, in one place.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              component={Link} to="/demo" variant="contained" size="large"
              sx={{ px: 4, py: 1.4, fontSize: '1rem', textTransform: 'none',
                borderRadius: 2, transition: 'transform var(--dur-base)',
                '&:hover': { transform: 'translateY(-1px)' } }}
            >
              Open the live demo
            </Button>
            <Button
              component={Link} to="/register" size="large"
              sx={{ px: 3, py: 1.4, fontSize: '1rem', textTransform: 'none',
                color: 'var(--text)' }}
            >
              Create an account
            </Button>
          </Box>

          <Typography sx={{ mt: 2.5, fontSize: '0.9rem', color: 'var(--text-faint)' }}>
            No signup for the demo. Two years of real history &mdash; change anything you like.
          </Typography>
        </Box>
      </Container>

      </Box>

      {/* ── Proof. Numbers before claims. ───────────────────────────────── */}
      <Box sx={{ borderTop: '1px solid var(--border-soft)',
        borderBottom: '1px solid var(--border-soft)' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
          <Box sx={{ display: 'grid', gap: { xs: 4, md: 3 },
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' } }}>
            {[
              { n: 'Every account', l: 'Chequing, investments, property, loans \u2014 in one place' },
              { n: 'Both of you', l: 'One view of the household, not two logins' },
              { n: 'Tax-time ready', l: 'Your gains worked out the way Canada expects' },
              { n: 'Read-only', l: 'It can never move your money' },
            ].map((s) => (
              <Box key={s.n}>
                <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', mb: 0.75 }}>
                  {s.n}
                </Typography>
                <Typography sx={{ fontSize: '0.9rem', color: 'var(--text-secondary)',
                  lineHeight: 1.5 }}>
                  {s.l}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── What it does. Outcomes, not features. ───────────────────────── */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 13 } }}>
        <Typography component="h2" sx={{
          fontSize: { xs: '2rem', md: '2.6rem' }, fontWeight: 700,
          letterSpacing: '-0.03em', mb: 2, maxWidth: 640,
        }}>
Numbers you can actually trust.
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', maxWidth: 640, mb: { xs: 6, md: 9 },
          fontSize: '1.05rem', lineHeight: 1.6 }}>
          When something moves, you can see exactly what moved it. No mystery
          jumps, no numbers that quietly disagree with your statements.
        </Typography>

        <Box sx={{ display: 'grid', gap: { xs: 6, md: 7 },
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
          {[
            {
              h: 'Ready when taxes are',
              p: 'What you paid for every holding, tracked across accounts the way Canada expects. So when you sell, the gain is already worked out \u2014 not something you reconstruct from a shoebox in April.',
            },
            {
              h: 'Matches your statements',
              p: 'Drop in a file from your brokerage and the holdings, the cash and the cost line up with what they show you. When something cannot line up, it tells you which number and why.',
            },
            {
              h: 'Built for two',
              p: 'Both of you, every account, one number. Most money apps charge per person and then make you compare screenshots \u2014 your partner is not an upsell here.',
            },
            {
              h: 'Returns without the flattery',
              p: 'Time-weighted returns and XIRR, benchmarked honestly. Every figure says how it was computed, and when the data cannot support a number, it says that instead of inventing one.',
            },
            {
              h: 'Room you can act on',
              p: 'TFSA and RRSP room counted from the deposits actually in your ledger \u2014 not a tally you keep in your head \u2014 so \u201ccan I still contribute?\u201d has an answer before the penalty does.',
            },
          ].map((c) => (
            <Box key={c.h}>
              <Typography component="h3" sx={{ fontWeight: 650, fontSize: '1.15rem', mb: 1.5 }}>
                {c.h}
              </Typography>
              <Typography sx={{ color: 'var(--text-secondary)', lineHeight: 1.65 }}>
                {c.p}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>

      {/* ── Candour. The most persuasive section on the page. ───────────── */}
      <Box sx={{ background: 'var(--surface-1, rgba(255,255,255,0.02))',
        borderTop: '1px solid var(--border-soft)',
        borderBottom: '1px solid var(--border-soft)' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 } }}>
          <Typography component="h2" sx={{
            fontSize: { xs: '1.7rem', md: '2.1rem' }, fontWeight: 700,
            letterSpacing: '-0.03em', mb: 3,
          }}>
            What FolioWorth will not do
          </Typography>
          <Box sx={{ display: 'grid', gap: { xs: 3, md: 5 },
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, maxWidth: 980 }}>
            {[
              'Move your money. It is read-only, always. No transfers, no trades.',
              'Tell you what to buy. It reports what you hold; it does not give advice.',
              'Make a number up to fill a gap. If it cannot work something out, it says so.',
            ].map((t) => (
              <Typography key={t} sx={{ color: 'var(--text-secondary)', lineHeight: 1.65 }}>
                {t}
              </Typography>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Pricing ─────────────────────────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ py: { xs: 7, md: 11 } }}>
        <Typography component="h2" sx={{
          fontSize: { xs: '1.8rem', md: '2.2rem' }, fontWeight: 700,
          letterSpacing: '-0.03em', mb: 1,
        }}>
          Pricing
        </Typography>
        <Typography sx={{ color: 'var(--text-secondary)', mb: 5, maxWidth: 560 }}>
          Priced per household, not per seat. Your partner is not an upsell.
        </Typography>

        {/* Detection is a guess; an explicit choice must win. */}
        <Box sx={{ display: 'flex', gap: 2, mb: 5 }}>
          {(['CAD', 'USD'] as Currency[]).map((c) => (
            <Box
              key={c}
              component="button"
              onClick={() => setCurrency(c)}
              sx={{
                background: 'none', border: 'none', cursor: 'pointer', p: 0,
                font: 'inherit', fontSize: '0.875rem',
                color: currency === c ? 'var(--text)' : 'var(--text-faint)',
                borderBottom: currency === c
                  ? '1px solid var(--text)' : '1px solid transparent',
              }}
            >
              {c}
            </Box>
          ))}
        </Box>

        <Box sx={{
          display: 'grid', gap: 3,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          columnGap: { md: 7 },
        }}>
          {TIERS.map((p) => (
            <Box key={p.name} sx={{
              borderTop: p.highlight
                ? '2px solid var(--inflow)'
                : '1px solid var(--border-soft)',
              pt: 2.5,
              display: 'flex', flexDirection: 'column',
            }}>
              <Typography sx={{ fontWeight: 650, fontSize: '1.05rem' }}>{p.name}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 1.5, mb: 0.5 }}>
                <Typography className="fw-num" sx={{
                  fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.03em',
                }}>
                  {formatPrice(p, currency)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--text-faint)' }}>
                  {formatCadence(p)}
                </Typography>
              </Box>
              <Typography sx={{ color: 'var(--text-secondary)', mb: 2.5 }}>{p.blurb}</Typography>

              <Box sx={{ flex: 1 }}>
                {p.points.map((pt) => (
                  <Box key={pt} sx={{ display: 'flex', gap: 1.25, py: 0.6 }}>
                    <Box sx={{
                      width: 4, height: 4, borderRadius: '50%', mt: '9px',
                      flexShrink: 0, background: 'var(--text-faint)',
                    }} />
                    <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>{pt}</Typography>
                  </Box>
                ))}
              </Box>

              <Button component={Link} to={p.to}
                variant={p.highlight ? 'contained' : 'outlined'}
                fullWidth sx={{ mt: 3 }}>
                {p.cta}
              </Button>
            </Box>
          ))}
        </Box>

        <Typography variant="caption" sx={{
          display: 'block', mt: 3, color: 'var(--text-faint)', maxWidth: 640,
        }}>
          Bank connections cost us per linked institution, which is the only
          reason they sit behind a paid tier. Everything that is purely software
          stays free.
        </Typography>
      </Container>

      {/* ── Close ───────────────────────────────────────────────────────── */}
      <Box sx={{ borderTop: "1px solid var(--border-soft)", py: { xs: 7, md: 10 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography sx={{
            fontSize: { xs: '1.7rem', md: '2.1rem' }, fontWeight: 700,
            letterSpacing: '-0.03em', mb: 2,
          }}>
            See it with real data first.
          </Typography>
          <Typography sx={{ color: 'var(--text-secondary)', mb: 4, maxWidth: 520, mx: 'auto' }}>
            The demo has two years of history, cards that revolve, and a payment
            that never posted. Break it however you like.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button component={Link} to="/demo" variant="contained" size="large">
              Open the demo
            </Button>
            <Button component={Link} to="/register" variant="outlined" size="large">
              Create an account
            </Button>
          </Box>
        </Container>
      </Box>

      <SiteFooter />
    </Box>
  )
}
